import {useEffect, useRef, useState} from "react";
import {signinRedirectCallback} from "../../config/teamsystemOidc";
import {useIdentityAuth} from "../../context/IdentityAuthContext";
import {exchangeToken} from "../../services/authService";
import {getApiErrorMessage} from "../../utils/apiError";

/**
 * Puente entre Teamsystem e Identity.
 *
 * Flujo:
 *   1. Procesa el retorno de Teamsystem (?code=...) → User con access_token (TS).
 *   2. Intercambia ese token en /auth/token → tokens internos de Identity.
 *   3. Registra los tokens en el contexto (completeLogin).
 *   4. Redirige a la URL guardada (o /dashboard).
 */
const OidcCallback = () => {
  const {completeLogin, login} = useIdentityAuth();
  const [error, setError] = useState(null);

  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const run = async () => {
      try {
        // 1. Retorno de Teamsystem → access_token de Teamsystem.
        const tsUser = await signinRedirectCallback();
        if (!tsUser?.access_token) {
          throw new Error("Teamsystem no devolvió access_token");
        }

        // 2. Token exchange → tokens internos de Identity.
        const tokens = await exchangeToken(tsUser.access_token);
        if (!tokens?.access_token) {
          throw new Error("El token exchange no devolvió access_token");
        }

        // 3. Persistir y marcar sesión activa.
        completeLogin(tokens);

        // 4. Redirigir a la URL de retorno.
        const returnUrl = sessionStorage.getItem("returnUrl") || "/dashboard";
        sessionStorage.removeItem("returnUrl");
        window.location.replace(returnUrl);
      } catch (err) {
        console.error("Error en el callback de autenticación:", err);
        setError(err);
      }
    };

    run();
  }, [completeLogin]);

  const containerStyle = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    flexDirection: "column",
    gap: "1rem"
  };

  const buttonStyle = {
    padding: "0.5rem 1rem",
    backgroundColor: "#1976d2",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer"
  };

  if (error) {
    const detail = getApiErrorMessage(error, "");
    const isNoLocalUser = /no local user/i.test(detail);

    if (isNoLocalUser) {
      return (
        <div style={containerStyle}>
          <div>Tu usuario de Teamsystem ID no tiene acceso a Identity Panel.</div>
          <div style={{fontSize: "0.8rem", color: "#666"}}>
            Si crees que es un error, contacta con soporte o inicia sesión con otra cuenta.
          </div>
          <button onClick={() => login("/dashboard", {forceLogin: true})} style={buttonStyle}>
            Volver a inicio de sesión
          </button>
        </div>
      );
    }

    return (
      <div style={containerStyle}>
        <div>Error en la autenticación</div>
        <div style={{fontSize: "0.8rem", color: "#666"}}>{detail}</div>
        <button onClick={() => window.location.replace("/acceso?error=oidc_callback_failed")} style={buttonStyle}>
          Volver al login
        </button>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div>Procesando autenticación...</div>
      <div style={{fontSize: "0.8rem", color: "#666"}}>Espera mientras completamos tu inicio de sesión</div>
    </div>
  );
};

export default OidcCallback;
